import { useCallback } from 'react'
import { useOrgParams } from '~/common/use-org-params';
import { supabaseGetter } from "~/api/supabase-getter"
import { useCreateCrop, useCreateCropsBulk, useDeleteCrop, useCreateCropPhase, useCreateCropPhasesBulk, useCreateCropSchedule, useCreateCropSchedulesBulk, useCreateTasksFromSchedule, useCreateTasksFromSchedulesBulk, useCreateTasksBulk } from "~/api/supabase-mutations"
import { useCropsContextDispatch, cropAdded } from '~/common/context/crops/crops-context';
import _ from 'lodash';
import { useCurrentUser } from '~/common/use-current-user';
import { parseIntervalToDays } from '~/common/utils/date-utils';

/**
 * Add days to an ISO date string
 */
const addDaysToISO = (isoString, days) => {
  if (!isoString) return isoString
  const d = new Date(isoString)
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

export const useCreateNewCrop = () => {
  const { farmId, plotId, patchId, seasonId } = useOrgParams()
  const { currentUser } = useCurrentUser()
  const cropsDispatch = useCropsContextDispatch()
  const { createSchedule } = useCreateCropSchedule()
  const { createSchedulesBulk } = useCreateCropSchedulesBulk()
  const { createCrop } = useCreateCrop()
  const { createCropsBulk } = useCreateCropsBulk()
  const { deleteCrop } = useDeleteCrop()
  const { createPhase } = useCreateCropPhase()
  const { createPhasesBulk } = useCreateCropPhasesBulk()
  const { createTasks } = useCreateTasksFromSchedule()
  const { createTasksFromSchedulesBulk } = useCreateTasksFromSchedulesBulk()
  const { createTasksBulk } = useCreateTasksBulk()

  const createNewCrop = useCallback(async ({
    species,
    variety,
    archetype,
    count,
    growthType,
    lifecycleType,
    notes,
    orgId = null,
    propagationCodes = [],
    successions = null,
    firstFrostDate = null,
    phases,
    tasks = null,
    skipTaskTypes = [],
    isCustomCrop = false,
    customSpeciesName = '',
    isCustomVariety = false,
    customVarietyName = '',
    customFamilyCode = null,
  }) => {
    // org pointer hierarchy
    const orgIdHierarchy = orgId || patchId || plotId || farmId

    console.log("createNewCrop called:", {
      orgId: orgIdHierarchy,
      species,
      variety,
      archetype,
      count,
      growthType,
      lifecycleType,
      notes,
      phases: phases?.length || 0,
      tasks: tasks ? tasks.length : 'auto-generate',
      skipTaskTypes: skipTaskTypes?.length || 0,
    })

    if (!isCustomCrop && !species) return
    if (isCustomCrop && !customSpeciesName?.trim()) return

    // Track crop ID outside try so the catch block can roll back
    let newCropId

    try {
      const resolvedSpeciesName = isCustomCrop ? customSpeciesName.trim() : species.name
      const resolvedVarietyName = isCustomVariety ? customVarietyName.trim() : (variety?.name ?? '')
      const resolvedName = resolvedVarietyName
        ? `${resolvedSpeciesName} - ${resolvedVarietyName}`
        : resolvedSpeciesName

      const { data: crop, error: cropError } = await createCrop({
        organization_id: orgIdHierarchy,
        parent_crop_id: null,
        template_id: isCustomCrop ? null : (variety?.id ?? species.id),
        created_by: currentUser.id,
        family_code: isCustomCrop ? customFamilyCode : species.family_code,
        archetype_code: isCustomCrop ? (archetype?.code ?? null) : archetype.code,
        species_name: resolvedSpeciesName,
        variety_name: resolvedVarietyName,
        name: resolvedName,
        count,
        notes,
        growth_code: growthType,
        lifecycle_code: lifecycleType,
      })
      if (cropError) throw cropError
      newCropId = crop.id

      const { data: schedule, error: scheduleError } = await createSchedule({
        crop_id: newCropId,
        created_by: currentUser.id,
        season_id: seasonId ?? null,
      })
      if (scheduleError) throw scheduleError
      const newScheduleId = schedule.id

      const phaseResults = await Promise.all(phases.map(phase =>
        createPhase({
          schedule_id: newScheduleId,
          // Only include fields that exist in crop_phases table schema
          ..._.pick(phase, [
            'phase_type_code',
            'est_started_at',
            'est_duration',
            'est_elapsed_time',
            'started_at',
            'elapsed_time',
            'step',
            'requirements',
            'is_active',
            'is_complete',
            'completed_at',
            'notes'
          ])
        })
      ))
      const phaseError = phaseResults.find(r => r.error)?.error
      if (phaseError) throw phaseError

      // Create tasks: either use provided adjusted tasks or auto-generate from schedule
      if (tasks && tasks.length > 0) {
        // Use adjusted tasks from preview (user dragged task boundaries)
        // Filter out UI-specific fields (label, type, start, len) that exist for timeline rendering
        const tasksWithIds = tasks.map(task => ({
          // Only include valid database columns (omit 'id' — let DB generate it)
          ..._.pick(task, [
            'task_code',
            'task_window_start',
            'duration',
            'state',
            'notes',
            'completed_by',
            'completed_at'
          ]),
          crop_id: newCropId,
          organization_id: orgIdHierarchy,
          created_by: currentUser.id,
          season_id: seasonId ?? null,
        }))

        const { error: tasksError } = await createTasksBulk(tasksWithIds)
        if (tasksError) throw tasksError
        console.log(`Created ${tasksWithIds.length} adjusted tasks for crop ${newCropId}`)
      } else {
        // Auto-generate tasks from schedule data (default behavior)
        const scheduleData = {
          crop: {
            id: newCropId,
            organization_id: orgIdHierarchy,
            template_crop_species: isCustomCrop
              ? { name: resolvedSpeciesName, propagation_codes: propagationCodes }
              : { ...species, propagation_codes: propagationCodes },
          },
          schedule: {
            id: newScheduleId,
            crop_id: newCropId,
            state: 'active'
          },
          phases: phases.map(phase => ({
            // Use same filtered fields as phase creation to avoid transformed fields
            schedule_id: newScheduleId,
            ..._.pick(phase, [
              'phase_type_code',
              'est_started_at',
              'est_duration',
              'est_elapsed_time',
              'step'
            ])
          }))
        }

        const { data: generatedTasks, error: tasksError } = await createTasks(scheduleData, {
          userId: currentUser.id,
          skipTaskTypes,
          seasonId: seasonId ?? null,
        })
        if (tasksError) throw tasksError
        console.log(`Created ${generatedTasks?.length || 0} auto-generated tasks for crop ${newCropId}`)
      }

      // Create succession child crops
      if (successions?.enabled && successions.count >= 1) {
        const intervalDays = successions.intervalDays ?? 14

        // Bulk insert all succession crop records at once
        const successionCropsData = Array.from({ length: successions.count }, (_, i) => ({
          organization_id: orgIdHierarchy,
          parent_crop_id: newCropId,
          succession: i + 1,
          succession_interval_days: (i + 1) * intervalDays,
          created_by: currentUser.id,
          species_name: resolvedSpeciesName,
          variety_name: resolvedVarietyName,
          name: resolvedName,
          count,
        }))

        const { data: childCrops, error: childCropsError } = await createCropsBulk(successionCropsData)
        if (childCropsError) throw childCropsError

        // Bulk insert all schedules
        const { data: childSchedules, error: schedulesError } = await createSchedulesBulk(
          childCrops.map(childCrop => ({
            crop_id: childCrop.id,
            created_by: currentUser.id,
            season_id: seasonId ?? null,
          }))
        )
        if (schedulesError) throw schedulesError

        // Bulk insert all phases and generate+insert all tasks in parallel (independent)
        const [{ error: phasesError }, { error: tasksError }] = await Promise.all([
          createPhasesBulk(
            childCrops.flatMap((_childCrop, i) => {
              const offsetDays = (i + 1) * intervalDays
              return phases.map(phase => {
                const newStartAt = addDaysToISO(phase.est_started_at, offsetDays)
                let estDuration = phase.est_duration

                // For indeterminate crops, clamp the mature phase so it doesn't extend past ffd
                if (growthType === 'indeterminate' && phase.phase_type_code === 'mature' && firstFrostDate) {
                  const matureStart = new Date(newStartAt)
                  const daysUntilFrost = Math.floor((firstFrostDate - matureStart) / (1000 * 60 * 60 * 24))
                  const originalDays = parseIntervalToDays(phase.est_duration)
                  const clampedDays = Math.max(1, Math.min(originalDays, daysUntilFrost))
                  estDuration = `${clampedDays} days`
                }

                return {
                  ..._.pick(phase, [
                    'phase_type_code', 'est_elapsed_time', 'started_at', 'elapsed_time',
                    'step', 'requirements', 'is_active', 'is_complete', 'completed_at', 'notes'
                  ]),
                  schedule_id: childSchedules[i].id,
                  est_started_at: newStartAt,
                  est_duration: estDuration,
                }
              })
            })
          ),
          createTasksFromSchedulesBulk(
            childCrops.map((childCrop, i) => {
              const offsetDays = (i + 1) * intervalDays
              return {
                crop: {
                  id: childCrop.id,
                  organization_id: orgIdHierarchy,
                  template_crop_species: isCustomCrop
                    ? { name: resolvedSpeciesName, propagation_codes: propagationCodes }
                    : { ...species, propagation_codes: propagationCodes },
                },
                schedule: { id: childSchedules[i].id, crop_id: childCrop.id, state: 'active' },
                phases: phases.map(phase => ({
                  schedule_id: childSchedules[i].id,
                  ..._.pick(phase, ['phase_type_code', 'est_started_at', 'est_duration', 'est_elapsed_time', 'step']),
                  est_started_at: addDaysToISO(phase.est_started_at, offsetDays),
                }))
              }
            }),
            { userId: currentUser.id, skipTaskTypes, seasonId: seasonId ?? null }
          ),
        ])
        if (phasesError) throw phasesError
        if (tasksError) throw tasksError

        console.log(`Created ${successions.count} succession child crops for crop ${newCropId}`)
      }

      // Invalidate caches and notify listeners to refetch
      // Crops, schedules, phases, and tasks are used by crop lists and timeline views
      supabaseGetter.crops.invalidate()
      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropPhases.invalidate()
      supabaseGetter.cropDetails.invalidate()
      supabaseGetter.tasks.invalidate()
      cropsDispatch(cropAdded({ cropId: newCropId }))
    } catch (error) {
      console.error('createNewCrop failed, rolling back inserts:', error)

      // Deleting the crop cascades to: crop_schedules → crop_phases, and tasks (via crop_id FK)
      if (newCropId) {
        await deleteCrop(newCropId)
        supabaseGetter.crops.invalidate()
        supabaseGetter.cropSchedules.invalidate()
        supabaseGetter.cropPhases.invalidate()
        supabaseGetter.tasks.invalidate()
      }

      throw error
    }
  }, [
    currentUser,
    createCrop,
    createCropsBulk,
    deleteCrop,
    createPhase,
    createPhasesBulk,
    createSchedule,
    createSchedulesBulk,
    createTasks,
    createTasksBulk,
    createTasksFromSchedulesBulk,
    cropsDispatch,
    farmId,
    patchId,
    plotId,
    seasonId,
  ])

  return { createNewCrop }
}
